<script setup lang="ts">
/**
 * The application for the beta (ADR-143), the form portal and website open in
 * their inquiry dialog where "Zugang anfragen" stood (TL, 2026-10-27). The questions follow the
 * design prototype's form (`betaFormHtml`, 2026-10-27): "Über euch" with first and last name,
 * function, e-mail, phone, organisation, country and whether dataCycle is in use; "Team und Ziele"
 * with the team's size, the areas and the goal; three checkboxes for feedback, the privacy notice
 * (required, linked to the app's privacy page in a new tab, so the form stays filled) and news.
 * Required fields carry an asterisk, the others "(optional)"; each field has its own error, shown
 * once it was left or a send was tried, and a send with errors leads the focus to the first one.
 * The values are checked with the contract (`applicationValueSchema`) and sent as its codes; the
 * form adds honeypot and `startedAt` (ADR-050 stage 1). The app passes the sending (`send`) and
 * decides what a failure says, except a `400` that names fields: the form marks those. After
 * sending it thanks by first name without promising a mail, since none is sent (TL, 2026-10-27),
 * and emits `sent`; the dialog around it, with its heading (`applicationHeading`), is the app's.
 * The thanks take the focus, which would otherwise fall to the page with the form (GU4).
 */
import { computed, nextTick, onMounted, ref } from "vue";
import { Field, useForm } from "vee-validate";
import { useI18n } from "vue-i18n";
import { toZodTypedSchema } from "@pixelpoint/astro-core/form/core/zodAdapter.ts";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import PixSelect from "@pixelpoint-pattern-library-v2/base/components/styled/base/inputs/PixSelect/PixSelect.vue";
import PixTextArea from "@pixelpoint-pattern-library-v2/base/components/styled/base/inputs/PixTextArea/PixTextArea.vue";
import PixTextField from "@pixelpoint-pattern-library-v2/base/components/styled/base/inputs/PixTextField/PixTextField.vue";
import {
  applicationBody,
  applicationChoices,
  applicationChoiceText,
  applicationErrorKey,
  applicationValueSchema,
  type ApplicationChoice,
  type ApplicationField,
} from "./application.ts";
import CheckField from "./CheckField.vue";
import ChoiceGroup from "./ChoiceGroup.vue";
import type { InquirySend } from "./inquiry.ts";

const props = defineProps<{
  send: InquirySend;
  /** The app's privacy page; without it the privacy notice has no link. */
  privacyHref?: string | undefined;
  /** Shows "Abbrechen" beside the send button; the app closes on `cancel`. */
  cancelable?: boolean | undefined;
}>();

const emit = defineEmits<{ sent: []; cancel: [] }>();

const { t } = useI18n();

type TextField = { name: ApplicationField; type?: "email" | "tel"; required: boolean; placeholder?: boolean };

/** The text fields of "Über euch" in rows, two side by side from the small breakpoint on. */
const textRows: TextField[][] = [
  [
    { name: "firstName", required: true },
    { name: "lastName", required: true },
  ],
  [{ name: "jobTitle", required: true, placeholder: true }],
  [
    { name: "email", type: "email", required: true },
    { name: "phone", type: "tel", required: false, placeholder: true },
  ],
  [{ name: "company", required: true, placeholder: true }],
];

const options = (field: ApplicationChoice) =>
  applicationChoices[field].map((code) => ({ value: code, label: applicationChoiceText(field, code, t) }));

/** A segment's value as vee-validate holds it: its code, or none chosen yet. */
const codeOf = (value: unknown) => (typeof value === "string" ? value : undefined);
/** The chips' value: the codes chosen. */
const codesOf = (value: unknown): readonly string[] => (Array.isArray(value) ? value : []);

const { handleSubmit, isSubmitting, submitCount, setFieldError } = useForm({
  validationSchema: toZodTypedSchema(applicationValueSchema),
  initialValues: { areas: [], feedbackReady: false, newsletter: false, privacyAccepted: false },
});

// Bot protection (ADR-050): a field people never see, and when the form was shown — on the
// client, so a page rendered ahead of time does not date it.
const website = ref("");
const startedAt = ref("");
onMounted(() => {
  startedAt.value = new Date().toISOString();
});

const form = ref<HTMLFormElement | null>(null);
const sent = ref(false);
/** The thanks, which take the focus after sending, as the form that had it is gone (GU4). */
const thanks = ref<HTMLElement | null>(null);
/** The first name the thanks address. */
const thanked = ref("");
const failure = ref("");
// One application at a time: a second submit (Enter in a field) while the first is on its way is dropped.
let sending = false;

/** The focus on the first field with an error, once the errors are on screen. */
async function focusFirstError() {
  await nextTick();
  form.value?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
}

const fieldNames = new Set<string>(Object.keys(applicationValueSchema.shape));

/** Marks the fields the API refused that the form shows, each with its own error; whether there was one. */
function markRefused(refused: readonly string[] = []): boolean {
  const shown = refused.filter((name) => fieldNames.has(name));
  for (const name of shown) setFieldError(name as ApplicationField, name);
  return shown.length > 0;
}

const onSubmit = handleSubmit(async (values) => {
  if (sending) return;
  failure.value = "";
  const body = applicationBody(values as Record<string, unknown>, {
    website: website.value,
    startedAt: startedAt.value,
  });
  if (!body || body.kind !== "application") {
    failure.value = t("inquiryForm.failed");
    return;
  }
  sending = true;
  try {
    const result = await props.send(body);
    if (result.ok) {
      thanked.value = body.firstName;
      sent.value = true;
      emit("sent");
      await nextTick();
      thanks.value?.focus();
    } else if (markRefused(result.fields)) {
      failure.value = t("inquiryForm.invalid");
      await focusFirstError();
    } else {
      failure.value = result.message;
    }
  } finally {
    sending = false;
  }
}, focusFirstError);

/** The field's error in the person's language, once it was left or a send was tried. */
const errorOf = (name: string, message: string | undefined, touched: boolean) =>
  message && (touched || submitCount.value > 0) ? t(applicationErrorKey(name)) : "";

const submitText = computed(() => t(isSubmitting.value ? "applicationForm.sending" : "applicationForm.submit"));

const setClass = "m-0 flex min-w-0 flex-col gap-3.5 border-0 p-0";
const dividerClass = "border-line-soft mt-1 border-t pt-5";
const legendClass = "text-card text-text-head mb-2.5 p-0 font-extrabold";
const rowClass = { pair: "grid gap-3.5 sm:grid-cols-2", single: "flex flex-col" };
const linkClass =
  "rounded-chip text-accent-text focus-visible:outline-focus font-semibold underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2";
/** The prototype's buttons: 40 pixels, 44 on touch screens, the control radius; send with the brand's fill. */
const buttonBase =
  "text-ui min-h-(--size-field) px-4 font-semibold pointer-coarse:min-h-(--size-button) [--pix-button-border-hover:transparent] [--pix-button-border:transparent] [--pix-button-radius:var(--radius-control)]";
const sendClass =
  "bg-origin-border [--pix-button-bg:var(--gradient-brand-fill)] [--pix-button-bg-hover:var(--gradient-brand-fill)] [--pix-button-text:var(--color-on-brand)] [--pix-button-text-hover:var(--color-on-brand)] hover:brightness-110";
const cancelClass =
  "[--pix-button-bg:var(--color-fill)] [--pix-button-bg-hover:var(--color-fill-2)] [--pix-button-text:var(--color-text-head)] [--pix-button-text-hover:var(--color-text-head)]";
</script>

<template>
  <div
    v-if="sent"
    ref="thanks"
    role="status"
    tabindex="-1"
    class="rounded-control focus-visible:outline-focus flex flex-col items-start focus-visible:outline-2 focus-visible:outline-offset-2"
    data-application-sent
  >
    <span
      class="rounded-card text-on-brand flex size-14 items-center justify-center [background-image:var(--gradient-brand-fill)]"
      aria-hidden="true"
    >
      <svg class="size-6" viewBox="0 0 24 24">
        <path
          d="M21.5 2.5 2.8 10.2l7 2.6 2.6 7 9.1-17.3Z M9.8 12.8l11.7-10.3"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linejoin="round"
        />
      </svg>
    </span>
    <p class="text-title text-text-head m-0 mt-5 font-extrabold">{{ t("applicationForm.sent.title") }}</p>
    <p class="text-body text-text-body-2 m-0 mt-2">{{ t("applicationForm.sent.text", { name: thanked }) }}</p>
  </div>
  <form
    v-else
    ref="form"
    class="flex flex-col gap-5 [--pix-field-font-size:var(--text-ui)] [--pix-field-label-weight:600] pointer-coarse:[--pix-field-font-size:var(--text-body)] [&_[data-field]_label:not([data-required])::after]:font-normal [&_[data-field]_label:not([data-required])::after]:content-['_(optional)']"
    novalidate
    data-application-form
    @submit="onSubmit"
  >
    <fieldset :class="setClass">
      <legend :class="legendClass">{{ t("applicationForm.about") }}</legend>
      <div v-for="(row, index) in textRows" :key="index" :class="row.length > 1 ? rowClass.pair : rowClass.single">
        <Field
          v-for="field in row"
          :key="field.name"
          v-slot="{ value, handleChange, handleBlur, errorMessage, meta }"
          :name="field.name"
        >
          <PixTextField
            :model-value="(value as string | undefined) ?? ''"
            :type="field.type ?? 'text'"
            :label="t(`applicationForm.${field.name}.label`)"
            :placeholder="field.placeholder ? t(`applicationForm.${field.name}.placeholder`) : ''"
            :required="field.required"
            :error="errorOf(field.name, errorMessage, meta.touched)"
            :data-field="field.name"
            @update:model-value="handleChange"
            @blur="handleBlur"
          />
        </Field>
      </div>
      <Field v-slot="{ value, handleChange, errorMessage, meta }" name="country">
        <PixSelect
          :model-value="(value as string | undefined) ?? ''"
          :items="options('country')"
          :label="t('applicationForm.country.label')"
          :placeholder="t('inquiryForm.choose')"
          required
          :error="errorOf('country', errorMessage, meta.touched)"
          data-field="country"
          @update:model-value="handleChange"
        />
      </Field>
      <Field v-slot="{ value, handleChange, handleBlur, errorMessage, meta }" name="datacycleUse">
        <ChoiceGroup
          :model-value="codeOf(value)"
          :options="options('datacycleUse')"
          :label="t('applicationForm.datacycleUse.label')"
          required
          :error="errorOf('datacycleUse', errorMessage, meta.touched)"
          data-choice="datacycleUse"
          @update:model-value="handleChange"
          @blur="handleBlur"
        />
      </Field>
    </fieldset>

    <!-- the line above the second part on a wrapper: a fieldset's border runs beside its legend -->
    <div :class="dividerClass">
      <fieldset :class="setClass">
        <legend :class="legendClass">{{ t("applicationForm.team") }}</legend>
        <Field v-slot="{ value, handleChange, handleBlur, errorMessage, meta }" name="teamSize">
          <ChoiceGroup
            :model-value="codeOf(value)"
            :options="options('teamSize')"
            :label="t('applicationForm.teamSize.label')"
            required
            :error="errorOf('teamSize', errorMessage, meta.touched)"
            data-choice="teamSize"
            @update:model-value="handleChange"
            @blur="handleBlur"
          />
        </Field>
        <Field v-slot="{ value, handleChange, errorMessage }" name="areas">
          <ChoiceGroup
            :model-value="codesOf(value)"
            :options="options('areas')"
            :label="t('applicationForm.areas.label')"
            :hint="t('applicationForm.areas.hint')"
            multiple
            :error="errorOf('areas', errorMessage, true)"
            data-choice="areas"
            @update:model-value="handleChange"
          />
        </Field>
        <Field v-slot="{ value, handleChange, handleBlur, errorMessage, meta }" name="message">
          <PixTextArea
            :model-value="(value as string | undefined) ?? ''"
            :label="t('applicationForm.message.label')"
            :placeholder="t('applicationForm.message.placeholder')"
            :error="errorOf('message', errorMessage, meta.touched)"
            :rows="4"
            data-field="message"
            @update:model-value="handleChange"
            @blur="handleBlur"
          />
        </Field>
      </fieldset>
    </div>

    <div class="border-line-soft flex flex-col gap-3 border-t pt-5">
      <Field v-slot="{ value, handleChange }" name="feedbackReady">
        <CheckField :model-value="value === true" data-check="feedbackReady" @update:model-value="handleChange">
          {{ t("applicationForm.feedbackReady.label") }}
          <span class="text-text-soft">{{ t("applicationForm.optional") }}</span>
        </CheckField>
      </Field>
      <Field v-slot="{ value, handleChange, handleBlur, errorMessage, meta }" name="privacyAccepted">
        <CheckField
          :model-value="value === true"
          required
          :error="errorOf('privacyAccepted', errorMessage, meta.touched)"
          data-check="privacyAccepted"
          @update:model-value="handleChange"
          @blur="handleBlur"
        >
          {{ t("applicationForm.privacyAccepted.before") }}
          <a v-if="props.privacyHref" :href="props.privacyHref" target="_blank" rel="noopener" :class="linkClass">{{
            t("inquiryForm.privacy.link")
          }}</a>
          <template v-else>{{ t("inquiryForm.privacy.link") }}</template>
          {{ t("applicationForm.privacyAccepted.after") }}
        </CheckField>
      </Field>
      <Field v-slot="{ value, handleChange }" name="newsletter">
        <CheckField :model-value="value === true" data-check="newsletter" @update:model-value="handleChange">
          {{ t("applicationForm.newsletter.label") }}
          <span class="text-text-soft">{{ t("applicationForm.newsletter.optional") }}</span>
        </CheckField>
      </Field>
    </div>

    <!-- Honeypot (ADR-050): out of sight, out of the tab order and hidden from screen readers. -->
    <div class="sr-only" aria-hidden="true">
      <label>
        Website
        <input v-model="website" type="text" name="website" tabindex="-1" autocomplete="off" />
      </label>
    </div>

    <p v-if="failure" role="alert" class="text-ui text-bad-text m-0" data-application-failure>{{ failure }}</p>
    <div class="flex flex-wrap items-center justify-between gap-3" data-application-actions>
      <!-- the asterisk's legend for the eye; assistive technology hears "required" at each field -->
      <p class="text-meta text-text-soft m-0" aria-hidden="true">
        <span class="text-bad-text">*</span> {{ t("applicationForm.required") }}
      </p>
      <div class="flex flex-wrap gap-2 max-sm:w-full max-sm:flex-col-reverse">
        <PixButton
          v-if="props.cancelable"
          type="button"
          :class="[buttonBase, cancelClass]"
          data-action="cancel"
          @click="emit('cancel')"
        >
          {{ t("inquiryForm.cancel") }}
        </PixButton>
        <PixButton type="submit" :class="[buttonBase, sendClass]" :disabled="isSubmitting" data-action="send">
          <span class="inline-flex items-center gap-2">
            {{ submitText }}
            <svg class="size-5 flex-none" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M21.5 2.5 2.8 10.2l7 2.6 2.6 7 9.1-17.3Z M9.8 12.8l11.7-10.3"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </PixButton>
      </div>
    </div>
  </form>
</template>
